import { useEffect } from 'react';
import { overlayModule, useOverlayModule } from '../app/lazyOverlay';
import type { SearchPaletteProps } from './SearchPalette';

// The lazy boundary in front of the command palette (T-049; REQ-280 AC1, ARCH §9.5 / §12.1).
//
// T-044 left the palette eagerly imported by `AppShell`, so the matcher, the copy, the row markup and the
// result pills were in the app's FIRST chunk — about 35 KB of source that nobody who never searches will
// use. It is a clean boundary: the palette is behind `open` and does no work until then.
//
// The one thing that must not change is how `/` feels. A chunk fetched on the keypress would put a network
// round trip between the key and the caret, so the chunk is PREFETCHED ON IDLE after the shell has settled —
// the same pattern `PeriodProvider` uses for its own idle work (REQ-280 AC2). By the time anyone reaches for
// `/` the module is in memory and the palette is drawn in the same commit as the key; a cold, throttled first
// second is the one case where it is not, and then there is nothing on screen for a frame or two, on purpose:
// the palette is a modal over the page, and a spinner in front of a page that is already there would be worse
// than the 50 ms of nothing.
//
// T-323 / BUG-157: the boundary is `app/lazyOverlay.ts`, not `React.lazy` + `Suspense`. The two boundaries
// T-321 built to this pattern each ate a 4 GB heap — a boundary that suspends on the very press that mounts
// it livelocks React's synchronous `act()` flush, and the whole story is in that file's comment.
//
// **This one does not, and nobody knows why.** `app/shell/bugs.t049.test.tsx` presses `shell.search` and
// reads `search.palette` in 592 ms, through the same `fireEvent` → `act()` path that hangs forever on the
// date panel and the review dialog; whatever in React's heuristics decides to commit the fallback here and
// retry there was not worth chasing, because the only safe conclusion either way is that this is the same
// shape as the two that died. So it moves with them: one implementation, one behaviour, and no boundary
// left that can do it again. (That file is also the one `vite.config.ts` named as the heap's killer from
// T-049 until T-323; it runs 15 tests in 18 s at a 162 MB peak now, and whether it ever died of this is
// not established — only that it does not die of anything today.)

const palette = overlayModule(() => import('./SearchPalette'));

/** Warm the chunk when the browser is idle. Safe to call repeatedly: the load is shared. */
export function prefetchSearchPalette(): void {
  void palette.load();
}

/**
 * Ask for the chunk once the shell is idle. `requestIdleCallback` where it exists (every browser we ship to
 * but Safari), a short timeout where it does not, and the handle is cancelled on unmount so a test that
 * mounts and unmounts the shell does not leave a timer behind.
 */
export function useSearchPalettePrefetch(): void {
  useEffect(() => {
    const idle = (globalThis as { requestIdleCallback?: typeof requestIdleCallback })
      .requestIdleCallback;
    if (typeof idle === 'function') {
      const handle = idle(() => prefetchSearchPalette(), { timeout: 2000 });
      const cancel = (globalThis as { cancelIdleCallback?: typeof cancelIdleCallback })
        .cancelIdleCallback;
      return () => cancel?.(handle);
    }
    const timer = setTimeout(prefetchSearchPalette, 1200);
    return () => clearTimeout(timer);
  }, []);
}

/**
 * The palette, code-split. It renders nothing at all until it is opened, so a page that is never searched
 * neither downloads the chunk on the critical path nor mounts anything.
 */
export function LazySearchPalette(props: SearchPaletteProps) {
  const module = useOverlayModule(palette, props.open);
  if (!props.open || module === null) return null;
  const SearchPalette = module.SearchPalette;
  return <SearchPalette {...props} />;
}
